/**
 * Dashboard — Single-page business overview split into 3 sections:
 *
 * 1. Business Performance — KPI cards (always visible) + Monthly Revenue charts (collapsible)
 * 2. Distribution Performance — KPI cards (always visible) + Category/City/Brand charts (collapsible)
 * 3. Inventory Status — KPI cards (always visible) + Lifecycle/ABC/Alerts (collapsible)
 *
 * Each section has a hide/show toggle for the charts & data below the cards.
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Button,
} from '@mui/material';
import { format, startOfYear } from 'date-fns';
import {
  TrendingUp,
  TrendingDown,
  Refresh,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import api from '../../services/api';
import DrillDownModal from '../common/DrillDownModal';
import CollapsibleSection from './CollapsibleSection';
import { saveAs } from 'file-saver';
import DashboardFilterBar, { DashboardFilters } from './DashboardFilterBar';
import { DashboardSkeleton } from '../common/SkeletonLoaders';
import { EmptyState } from '../common/EmptyState';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import ExportMenu from '../Common/ExportMenu';
import { captureChartsAsPNGs, addChartSheet, addStyledDataSheet } from '../../utils/exportUtils';

/* ─── Types ─── */
interface QuickMetrics {
  total_stores: number;
  total_products: number;
  total_skus: number;
  pending_orders: number;
  critical_items: number;
  stockout_count: number;
  low_stock_items: number;
}

interface KPIMetrics {
  fill_rate_percentage: number;
  fill_rate_trend?: number;
  inventory_turns: number;
  inventory_turns_trend?: number;
  gross_margin_pct?: number;
  total_revenue: number;
  total_revenue_trend?: number;
  total_units_sold: number;
  total_units_trend?: number;
  stock_coverage_days: number;
  stock_coverage_months: number;
  stock_coverage_trend?: number;
  total_inventory_value: number;
  inventory_value_trend?: number;
}

interface DashboardMetrics {
  quick_metrics: QuickMetrics;
  kpis: KPIMetrics;
  revenue_analysis: any;
  fill_rate_analysis: any;
  breakdowns: any;
  working_capital?: any;
  cost_savings?: any;
}

/* ─── KPI Card ─── */
const KPICard: React.FC<{
  label: string;
  value: string;
  trend?: number;
  budgetTrend?: number;
  subtext?: string;
  onClick?: () => void;
  accent?: boolean;
}> = ({ label, value, trend, budgetTrend, subtext, onClick, accent }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: 'var(--color-surface)',
      border: `1px solid ${accent ? 'var(--color-accent)' : 'var(--color-border)'}`,
      borderRadius: 'var(--radius-md)',
      p: '14px 18px',
      boxShadow: 'var(--shadow-sm)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all var(--transition-fast)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      '&:hover': onClick ? { boxShadow: 'var(--shadow-md)', borderColor: 'var(--color-accent)' } : {},
    }}
  >
    <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
    {(trend !== undefined && trend !== null && trend !== 0) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        {trend > 0 ? <TrendingUp sx={{ fontSize: 18, color: 'var(--color-success)' }} /> : <TrendingDown sx={{ fontSize: 18, color: 'var(--color-danger)' }} />}
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: trend > 0 ? 'var(--color-success)' : 'var(--color-danger)', fontFamily: 'var(--font-body)' }}>
          {Math.abs(trend).toFixed(1)}%
        </Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 500, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
          vs last period
        </Typography>
      </Box>
    )}
    {(budgetTrend !== undefined && budgetTrend !== null) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
        {budgetTrend >= 0 ? <TrendingUp sx={{ fontSize: 14, color: 'var(--color-success)' }} /> : <TrendingDown sx={{ fontSize: 14, color: 'var(--color-danger)' }} />}
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: budgetTrend >= 0 ? 'var(--color-success)' : 'var(--color-danger)', fontFamily: 'var(--font-body)' }}>
          {budgetTrend >= 0 ? '+' : ''}{budgetTrend.toFixed(1)}%
        </Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 500, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
          vs budget
        </Typography>
      </Box>
    )}
    {subtext && !trend && (
      <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
        {subtext}
      </Typography>
    )}
  </Box>
);

/* ─── Helpers ─── */
const fmtNumber = (v: number | undefined) => {
  if (v === undefined || v === null || isNaN(v)) return '0';
  return v.toLocaleString('en-US');
};

const fmtPct = (v: number | undefined) => {
  if (v === undefined || v === null || isNaN(v)) return '0%';
  return `${v.toFixed(1)}%`;
};

const chartTooltipStyle = {
  background: '#FDFBF7',
  border: '1px solid #E8E0D4',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
  fontSize: '12px',
  padding: '10px 14px',
};

/* ─── Criticality ─── */
type Criticality = 'green' | 'orange' | 'red';

const CRITICALITY_META: Record<Criticality, { color: string; bg: string; label: string }> = {
  green: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', label: 'Under control' },
  orange: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', label: 'Watch out' },
  red: { color: 'var(--color-danger)', bg: 'var(--color-danger-bg)', label: 'Immediate action' },
};

/* A brand needs attention when its revenue is shrinking — the sharper the drop,
   the higher the business impact, so deep declines are flagged for immediate
   action while any decline is at least worth watching. */
const brandCriticality = (brand: any): Criticality => {
  const growth = Number(brand?.growth ?? 0);
  if (growth <= -15) return 'red';
  if (growth < 0) return 'orange';
  return 'green';
};

/* A card rolls up to its worst-case brand: one brand needing immediate action
   makes the whole card red. */
const rollupCriticality = (brands: any[]): Criticality => {
  if (brands.some((b) => brandCriticality(b) === 'red')) return 'red';
  if (brands.some((b) => brandCriticality(b) === 'orange')) return 'orange';
  return 'green';
};

/* ─── Brand Criticality Card ─── */
const BrandCriticalityCard: React.FC<{
  title: string;
  brands: any[];
  filterSummary?: string;
  fmtCurrency: (v: number | undefined) => string;
}> = ({ title, brands, filterSummary, fmtCurrency }) => {
  const [open, setOpen] = useState(false);
  const status = rollupCriticality(brands);
  const meta = CRITICALITY_META[status];
  const atRisk = brands.filter((b) => brandCriticality(b) !== 'green');
  const revenueAtRisk = atRisk.reduce((s: number, b: any) => s + (b.revenue || 0), 0);
  const total = brands.reduce((s: number, b: any) => s + (b.revenue || 0), 0);
  const impact =
    brands.length === 0
      ? 'No brands in scope'
      : status === 'green'
      ? 'All brands growing or stable'
      : `${fmtCurrency(revenueAtRisk)} revenue at risk · ${atRisk.length} brand${atRisk.length !== 1 ? 's' : ''}`;

  return (
    <Grid item xs={12} md={6} key={title}>
      <Paper sx={{ p: '16px 20px', border: '1px solid var(--color-border)', height: '100%' }}>
        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.25 }}>{title}</Typography>
        {filterSummary && (
          <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', mb: 1 }}>{filterSummary}</Typography>
        )}

        {/* Criticality banner — color-coded health of this card's brands */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 0.5,
            p: '8px 12px',
            bgcolor: meta.bg,
            borderLeft: `3px solid ${meta.color}`,
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: meta.color, lineHeight: 1.3 }}>
              {meta.label}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', lineHeight: 1.3 }}>
              {impact}
            </Typography>
          </Box>
        </Box>

        {/* Dropdown button — reveals the top 10 brands for this criticality card */}
        <Button
          fullWidth
          disableElevation
          onClick={() => setOpen((v) => !v)}
          disabled={brands.length === 0}
          endIcon={open ? <ExpandLess /> : <ExpandMore />}
          sx={{
            mt: 1,
            justifyContent: 'space-between',
            textTransform: 'none',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            px: 1.5,
            py: 0.5,
            '&:hover': { bgcolor: 'var(--color-surface-hover, rgba(0,0,0,0.03))' },
          }}
        >
          {open ? 'Hide top 10 brands' : 'Show top 10 brands'}
        </Button>

        <Collapse in={open} timeout="auto" unmountOnExit>
          {brands.length > 0 ? (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>#</TableCell>
                    <TableCell sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Brand</TableCell>
                    <TableCell align="right" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Revenue</TableCell>
                    <TableCell align="right" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Growth</TableCell>
                    <TableCell align="right" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brands.map((brand: any, idx: number) => {
                    const bStatus = brandCriticality(brand);
                    const bColor = CRITICALITY_META[bStatus].color;
                    return (
                      <TableRow key={brand.brand} hover sx={{ '& td': { py: 0.4 } }}>
                        <TableCell sx={{ fontSize: '11px', color: 'var(--color-text-muted)', width: 24 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: '11px', fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Tooltip title={CRITICALITY_META[bStatus].label} arrow>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: bColor, flexShrink: 0 }} />
                            </Tooltip>
                            {brand.brand}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(brand.revenue)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${brand.growth > 0 ? '+' : ''}${brand.growth}%`}
                            color={brand.growth > 0 ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '9px', height: 18 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '11px' }}>
                          {total > 0 ? ((brand.revenue / total) * 100).toFixed(1) + '%' : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ py: 2, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '12px' }}>No data</Typography>
          )}
        </Collapse>
      </Paper>
    </Grid>
  );
};

/* ─── Dashboard ─── */
const Dashboard: React.FC = () => {
  const { fmtCurrencyCompact: fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();

  /* Filter state */
  const [filters, setFilters] = useState<DashboardFilters>({
    brands: [],
    categories: [],
    stores: [],
    cities: [],
    fromDate: startOfYear(new Date()),
    toDate: new Date(),
  });

  /* Drill-down modal */
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownConfig, setDrillDownConfig] = useState<any>({ title: '', data: [] });
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // True only while an export is in progress — forces every CollapsibleSection
  // open so charts inside collapsed sections still have measurable dimensions
  // and can be captured by captureChartsAsPNGs.
  const [exporting, setExporting] = useState(false);

  /* Data queries */
  const { data: dashboardData, isLoading, isError, error, refetch } = useQuery<{ data: { data: DashboardMetrics } }>({
    queryKey: ['dashboard-main', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: async () => {
      const params = {
        countries: undefined as string[] | undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
        brands: filters.brands.length > 0 ? filters.brands : undefined,
        categories: filters.categories.length > 0 ? filters.categories : undefined,
        retailers: undefined as string[] | undefined,
        stores: filters.stores.length > 0 ? filters.stores : undefined,
        date_from: format(filters.fromDate, 'yyyy-MM-dd'),
        date_to: format(filters.toDate, 'yyyy-MM-dd'),
      };
      return api.getUnifiedDashboardMetrics(params, 'month');
    },
    refetchInterval: 300000,
  });

  /* Analytics data for Distribution charts (category, city, brands) */
  const { data: analyticsData } = useQuery({
    queryKey: ['dashboard-analytics', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: async () => {
      const f = {
        countries: [] as string[],
        brands: filters.brands,
        categories: filters.categories,
        retailers: [] as string[],
        stores: filters.stores,
        cities: filters.cities,
        dateFrom: filters.fromDate,
        dateTo: filters.toDate,
      };
      return api.getDashboardAllAnalytics(f, 'monthly');
    },
    refetchInterval: 300000,
    retry: 1,
  });

  /* Monthly Revenue Comparison (prev-year actuals / current-year actual+forecast / budget) */
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const { data: monthlyComparison } = useQuery<any[]>({
    queryKey: ['dashboard-monthly-comparison', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: async () => {
      return api.getDashboardMonthlyComparison({
        brands: filters.brands.length > 0 ? filters.brands : undefined,
        stores: filters.stores.length > 0 ? filters.stores : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
      });
    },
    refetchInterval: 300000,
    retry: 1,
  });

  /* Projection Forecast Summary (same algorithm as Forecasting → SKU Projection) */
  const { data: projectionSummary } = useQuery<{ monthly_forecast: Record<string, number>; monthly_historical: Record<string, number> }>({
    queryKey: ['dashboard-projection-summary', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: async () => {
      return api.getDashboardProjectionSummary({
        brands: filters.brands.length > 0 ? filters.brands : undefined,
        stores: filters.stores.length > 0 ? filters.stores : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
      });
    },
    refetchInterval: 300000,
    retry: 1,
  });

  /* Monthly Stock by Lifecycle */
  const { data: stockByLifecycle } = useQuery<{ data: any[]; groups: string[] }>({
    queryKey: ['dashboard-stock-lifecycle', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardMonthlyStockByLifecycle({
      brands: filters.brands.length > 0 ? filters.brands : undefined,
      stores: filters.stores.length > 0 ? filters.stores : undefined,
      cities: filters.cities.length > 0 ? filters.cities : undefined,
      dateFrom: format(filters.fromDate, 'yyyy-MM-dd'),
      dateTo: format(filters.toDate, 'yyyy-MM-dd'),
    }),
    refetchInterval: 300000,
    retry: 1,
  });

  /* Monthly Stock by ABC */
  const { data: stockByAbc } = useQuery<{ data: any[]; groups: string[] }>({
    queryKey: ['dashboard-stock-abc', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardMonthlyStockByAbc({
      brands: filters.brands.length > 0 ? filters.brands : undefined,
      stores: filters.stores.length > 0 ? filters.stores : undefined,
      cities: filters.cities.length > 0 ? filters.cities : undefined,
      dateFrom: format(filters.fromDate, 'yyyy-MM-dd'),
      dateTo: format(filters.toDate, 'yyyy-MM-dd'),
    }),
    refetchInterval: 300000,
    retry: 1,
  });

  /* Top brands — helper builds filter from current state on each call */
  const buildTopBrandsFilter = useCallback((cats?: string[]) => ({
    brands: filters.brands.length > 0 ? filters.brands : undefined,
    stores: filters.stores.length > 0 ? filters.stores : undefined,
    cities: filters.cities.length > 0 ? filters.cities : undefined,
    categories: cats || (filters.categories.length > 0 ? filters.categories : undefined),
    dateFrom: filters.fromDate,
    dateTo: filters.toDate,
  }), [filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate]);

  const { data: topBrandsMakeup } = useQuery({
    queryKey: ['dashboard-top-brands-makeup', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardTopBrands(buildTopBrandsFilter(['Makeup']), 10),
    refetchInterval: 300000, retry: 1,
  });
  const { data: topBrandsFragrance } = useQuery({
    queryKey: ['dashboard-top-brands-fragrance', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardTopBrands(buildTopBrandsFilter(['Fragrance']), 10),
    refetchInterval: 300000, retry: 1,
  });
  const { data: topBrandsSkincare } = useQuery({
    queryKey: ['dashboard-top-brands-skincare', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardTopBrands(buildTopBrandsFilter(['Skincare']), 10),
    refetchInterval: 300000, retry: 1,
  });

  /* Top brands — All Categories */
  const { data: topBrandsAll } = useQuery({
    queryKey: ['dashboard-top-brands-all', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: () => api.getDashboardTopBrands(buildTopBrandsFilter(), 10),
    refetchInterval: 300000, retry: 1,
  });

  const metrics = dashboardData?.data?.data;

  /* Derived data */
  const categoryData = analyticsData?.categoryData || [];
  const regionMarketShare = analyticsData?.countryRevenue?.map((item: any) => ({
    city: item.country || item.city,
    revenue: item.revenue,
    share: item.share,
  })) || [];
  const topBrands = (topBrandsAll || []).slice(0, 10);

  const revenueComparisonData = React.useMemo(() => {
    const monthly = monthlyComparison || [];
    if (monthly.length === 0) return [];
    const projForecast = projectionSummary?.monthly_forecast || {};
    // Use projection data as the forecast for future months
    return monthly.map((row: any) => {
      const projValue = projForecast[String(row.month_num)] || 0;
      return {
        ...row,
        // Replace current_year_forecast with projection data when available
        current_year_forecast: projValue > 0 ? projValue : (row.current_year_forecast || 0),
      };
    });
  }, [monthlyComparison, projectionSummary]);

  /* Forecast-derived KPIs */
  const forecastKpis = React.useMemo(() => {
    if (revenueComparisonData.length === 0) return null;
    let totalActual = 0;
    let totalForecast = 0;
    let totalBudget = 0;
    revenueComparisonData.forEach((r: any) => {
      totalActual += r.current_year_actual || 0;
      totalForecast += r.current_year_forecast || 0;
      totalBudget += r.budget || 0;
    });
    const fullYearProjection = totalActual + totalForecast;
    const budgetVariance = totalBudget > 0
      ? ((fullYearProjection - totalBudget) / totalBudget) * 100
      : undefined;
    return {
      fullYearProjection,
      forecastRemainder: totalForecast,
      budgetVariance,
    };
  }, [revenueComparisonData]);

  /* Cumulative sales comparison data */
  const cumulativeData = React.useMemo(() => {
    if (revenueComparisonData.length === 0) return [];
    let cumPrev = 0, cumActual = 0, cumForecast = 0, cumBudget = 0;
    return revenueComparisonData.map((r: any) => {
      cumPrev += (r.prev_year_actual || 0);
      cumActual += (r.current_year_actual || 0);
      cumForecast += (r.current_year_forecast || 0);
      cumBudget += (r.budget || 0);
      return {
        month: r.month,
        [`${previousYear}`]: Math.round(cumPrev),
        [`${currentYear} Actual + Forecast`]: Math.round(cumActual + cumForecast),
        [`Budget ${currentYear}`]: Math.round(cumBudget),
      };
    });
  }, [revenueComparisonData, currentYear, previousYear]);

  /* Revenue vs Budget variance (%) */
  const { data: budgetVarianceData } = useQuery({
    queryKey: ['dashboard-budget-variance', filters.brands, filters.categories, filters.stores, filters.cities, filters.fromDate, filters.toDate],
    queryFn: async () => {
      const fiscalYear = filters.fromDate.getFullYear();
      const brandId = filters.brands.length === 1 ? filters.brands[0] : undefined;
      const res = await api.getBudgetVariance(fiscalYear, brandId);
      return res.data;
    },
    refetchInterval: 300000,
    retry: 1,
  });

  const revenueBudgetVariance = useMemo(() => {
    if (!budgetVarianceData || !Array.isArray(budgetVarianceData) || budgetVarianceData.length === 0) return undefined;
    const totalBudget = budgetVarianceData.reduce((sum: number, b: any) => sum + (b.budget_revenue || 0), 0);
    const totalActual = budgetVarianceData.reduce((sum: number, b: any) => sum + (b.actual_revenue || 0), 0);
    if (!totalBudget) return undefined;
    return ((totalActual - totalBudget) / totalBudget) * 100;
  }, [budgetVarianceData]);

  const unitsBudgetVariance = useMemo(() => {
    if (!budgetVarianceData || !Array.isArray(budgetVarianceData) || budgetVarianceData.length === 0) return undefined;
    const totalBudgetUnits = budgetVarianceData.reduce((sum: number, b: any) => sum + (b.budget_units || 0), 0);
    const totalActualUnits = budgetVarianceData.reduce((sum: number, b: any) => sum + (b.actual_units || 0), 0);
    if (!totalBudgetUnits) return undefined;
    return ((totalActualUnits - totalBudgetUnits) / totalBudgetUnits) * 100;
  }, [budgetVarianceData]);

  /* Out-of-stock stats */
  const stockoutCount = metrics?.quick_metrics?.stockout_count || 0;
  const totalSkus = metrics?.quick_metrics?.total_skus || 0;
  const totalStores = metrics?.quick_metrics?.total_stores || 1;
  const totalPositions = totalSkus * totalStores;
  const stockoutPct = totalPositions > 0 ? ((stockoutCount / totalPositions) * 100).toFixed(1) : '0';

  /* Inventory quantity (sum of all lifecycle stage units) */
  const inventoryQuantity = metrics?.breakdowns?.lifecycle?.reduce(
    (sum: number, item: any) => sum + (item.total_units || 0), 0
  ) || 0;


  /* Drill-down handler */
  const handleKPIDrillDown = (kpiType: string) => {
    if (!metrics) return;
    const config: any = { title: kpiType, data: [], columns: [], insights: [] };
    switch (kpiType) {
      case 'revenue':
        config.title = 'Revenue Analysis';
        config.insights = metrics.kpis?.total_revenue_trend
          ? [{ id: '1', title: 'Revenue Trend', description: `Revenue ${metrics.kpis.total_revenue_trend > 0 ? 'increased' : 'decreased'} by ${Math.abs(metrics.kpis.total_revenue_trend).toFixed(1)}%`, severity: metrics.kpis.total_revenue_trend > 0 ? 'success' : 'warning' }]
          : [];
        break;
      case 'fillRate':
        config.title = 'Fill Rate Analysis';
        config.insights = metrics.fill_rate_analysis?.lost_sales_value
          ? [{ id: '1', title: 'Lost Sales', description: `Stockouts resulted in ${fmtCurrency(metrics.fill_rate_analysis.lost_sales_value)} in lost revenue.`, severity: 'warning' }]
          : [];
        break;
    }
    setDrillDownConfig(config);
    setDrillDownOpen(true);
  };

  /* Export dashboard data to Excel — produces a multi-sheet workbook
   * with the same branded styling as every other IRIS export. The first
   * sheet is the Monthly Revenue chart captured as an image; the rest
   * are styled data sheets routed through `addStyledDataSheet`. */
  const handleExport = useCallback(async () => {
    // Force every CollapsibleSection open so Recharts inside collapsed
    // sections has measurable layout and the SVGs can be captured.
    setExporting(true);
    // Two animation frames + a small delay let MUI's <Collapse> finish
    // its height transition and Recharts re-measure / redraw the SVGs.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 350);
        });
      });
    });

    try {
      const ExcelJS = await import('exceljs');

      const wb = new ExcelJS.Workbook();
      wb.creator = 'IRIS Platform';
      wb.created = new Date();

      const k = metrics?.kpis || {} as any;
      const q = metrics?.quick_metrics || {} as any;

      // ── Sheet 1: Chart(s) — capture every chart SVG inside the dashboard ──
      if (chartContainerRef.current) {
        const chartImages = await captureChartsAsPNGs(chartContainerRef.current);
        if (chartImages.length > 0) {
          addChartSheet(wb, { title: 'Dashboard Report', chartImages });
        } else {
          // eslint-disable-next-line no-console
          console.warn('Dashboard export: no chart SVG found in chartContainerRef');
        }
      }

    // ── Sheet 2: KPI Summary ──
    addStyledDataSheet(wb, {
      sheetName: 'KPI Summary',
      title: 'KPI Summary',
      rows: [
        { Metric: 'Total Revenue', Value: Number(k.total_revenue ?? 0), Trend: k.total_revenue_trend ?? '' },
        { Metric: 'Total Units Sold', Value: Number(k.total_units_sold ?? 0), Trend: k.total_units_trend ?? '' },
        { Metric: 'Fill Rate %', Value: Number(k.fill_rate_percentage ?? 0), Trend: k.fill_rate_trend ?? '' },
        { Metric: 'Inventory Turns', Value: Number(k.inventory_turns ?? 0), Trend: k.inventory_turns_trend ?? '' },
        { Metric: 'Gross Margin %', Value: k.gross_margin_pct != null ? Number(k.gross_margin_pct) : '', Trend: '' },
        { Metric: 'Stock Coverage (days)', Value: Number(k.stock_coverage_days ?? 0), Trend: k.stock_coverage_trend ?? '' },
        { Metric: 'Total Inventory Value', Value: Number(k.total_inventory_value ?? 0), Trend: k.inventory_value_trend ?? '' },
        { Metric: 'Total Stores', Value: Number(q.total_stores ?? 0), Trend: '' },
        { Metric: 'Total Products', Value: Number(q.total_products ?? 0), Trend: '' },
        { Metric: 'Stockout Count', Value: Number(q.stockout_count ?? 0), Trend: '' },
        { Metric: 'Low Stock Items', Value: Number(q.low_stock_items ?? 0), Trend: '' },
        { Metric: 'Pending Orders', Value: Number(q.pending_orders ?? 0), Trend: '' },
      ],
    });

    // ── Sheet 3: Monthly Revenue Comparison ──
    if (revenueComparisonData.length > 0) {
      addStyledDataSheet(wb, {
        sheetName: 'Revenue Comparison',
        title: `Monthly Revenue — ${previousYear} vs ${currentYear}`,
        rows: revenueComparisonData.map((r: any) => ({
          Month: r.month,
          [`${previousYear} Actual`]: Number(r.prev_year_actual || 0),
          [`${currentYear} Actual`]: Number(r.current_year_actual || 0),
          [`${currentYear} Forecast`]: Number(r.current_year_forecast || 0),
          [`Budget ${currentYear}`]: Number(r.budget || 0),
        })),
      });
    }

    // ── Sheet 4: Top Brands ──
    const brandData = [
      ...(topBrandsMakeup?.data || []),
      ...(topBrandsSkincare?.data || []),
      ...(topBrandsFragrance?.data || []),
    ];
    if (brandData.length > 0) {
      addStyledDataSheet(wb, {
        sheetName: 'Top Brands',
        title: 'Top Brands by Revenue',
        rows: brandData.map((b: any) => ({
          Brand: b.brand,
          Category: b.category || '',
          Revenue: Number(b.total_revenue || 0),
          'Units Sold': Number(b.total_quantity || 0),
        })),
      });
    }

    // ── Sheet 5: Category Breakdown ──
    const catBreakdown = analyticsData?.categoryData || [];
    if (catBreakdown.length > 0) {
      addStyledDataSheet(wb, {
        sheetName: 'Category Breakdown',
        title: 'Revenue by Category',
        rows: catBreakdown.map((c: any) => ({
          Category: c.category || c.name || '',
          Revenue: Number(c.revenue || c.value || 0),
          Quantity: Number(c.quantity || 0),
        })),
      });
    }

    // ── Sheet 6: Stock by Lifecycle ──
    if ((stockByLifecycle?.data?.length ?? 0) > 0) {
      addStyledDataSheet(wb, {
        sheetName: 'Stock by Lifecycle',
        title: 'Stock by Lifecycle Stage',
        rows: stockByLifecycle!.data as Record<string, any>[],
      });
    }

    // ── Sheet 7: Stock by ABC ──
    if ((stockByAbc?.data?.length ?? 0) > 0) {
      addStyledDataSheet(wb, {
        sheetName: 'Stock by ABC',
        title: 'Stock by ABC Classification',
        rows: stockByAbc!.data as Record<string, any>[],
      });
    }

      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const buf = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `Dashboard_Report_${dateStr}.xlsx`,
      );
    } finally {
      setExporting(false);
    }
  }, [metrics, revenueComparisonData, analyticsData, topBrandsMakeup, topBrandsSkincare, topBrandsFragrance, stockByLifecycle, stockByAbc, currentYear, previousYear]);

  /* Loading / Error */
  if (isLoading || !metrics) {
    return <Box sx={{ maxWidth: 'var(--content-max-width)' }}><DashboardSkeleton /></Box>;
  }
  if (isError) {
    return (
      <Box sx={{ maxWidth: 'var(--content-max-width)' }}>
        <EmptyState
          type="error"
          title="Failed to Load Dashboard"
          description={error instanceof Error ? error.message : 'Unable to load dashboard data'}
          actionLabel="Retry"
          onAction={() => refetch()}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 'var(--content-max-width)' }}>
      {/* ─── Title Row ─── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '16px' }}>
        <Box>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
            Business overview and key metrics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={() => refetch()} sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-text-primary)' } }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <ExportMenu
            data={revenueComparisonData.map((r: any) => ({
              Month: r.month,
              [`${previousYear} Actual`]: r.prev_year_actual || 0,
              [`${currentYear} Actual`]: r.current_year_actual || 0,
              [`${currentYear} Forecast`]: r.current_year_forecast || 0,
              [`Budget ${currentYear}`]: r.budget || 0,
            }))}
            filename="Dashboard_Report"
            title="Dashboard Report"
            sheetName="Revenue Comparison"
            chartRef={chartContainerRef}
            customExcelHandler={handleExport}
            onExportStart={() => setExporting(true)}
            onExportComplete={() => setExporting(false)}
            onExportError={() => setExporting(false)}
            label="Export Report"
          />
        </Box>
      </Box>

      {/* ─── Filter Bar ─── */}
      <DashboardFilterBar filters={filters} onChange={setFilters} />

      {/* Chart capture scope — every <svg> inside this container is embedded
          in the extracted report (see captureChartsAsPNGs). */}
      <div ref={chartContainerRef}>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ─── SECTION 1: Business Performance ─── */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Business Performance"
        forceOpen={exporting}
        headerContent={
          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            <Grid item xs={6} sm={2}>
              <KPICard label="Total Revenue" value={fmtCurrency(metrics.kpis?.total_revenue)} trend={metrics.kpis?.total_revenue_trend} budgetTrend={revenueBudgetVariance} onClick={() => handleKPIDrillDown('revenue')} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <KPICard label="Units Sold" value={fmtNumber(metrics.kpis?.total_units_sold)} trend={metrics.kpis?.total_units_trend} budgetTrend={unitsBudgetVariance} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <KPICard label="Full Year Projection" value={fmtCurrency(forecastKpis?.fullYearProjection)} budgetTrend={forecastKpis?.budgetVariance} subtext={forecastKpis ? `Forecast: ${fmtCurrency(forecastKpis.forecastRemainder)}` : 'Loading...'} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <KPICard label="Forecast (Remaining)" value={fmtCurrency(forecastKpis?.forecastRemainder)} subtext={`${12 - new Date().getMonth()} months remaining`} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <KPICard label="Gross Margin" value={fmtPct(metrics.kpis?.gross_margin_pct)} subtext="Revenue minus COGS" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <KPICard label="Avg Revenue/Store" value={fmtCurrency(metrics.quick_metrics?.total_stores > 0 ? (metrics.kpis?.total_revenue || 0) / metrics.quick_metrics.total_stores : 0)} subtext={`across ${fmtNumber(metrics.quick_metrics?.total_stores)} stores`} />
            </Grid>
          </Grid>
        }
      >
        {/* Monthly Revenue Charts */}
        <Paper sx={{ p: '20px', border: '1px solid var(--color-border)' }}>
          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
            {previousYear} Actuals vs {currentYear} Actuals / Forecast vs Budget
          </Typography>
          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#8B7355', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> {previousYear} Actuals
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#722F37', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> {currentYear} Actual
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#B76E79', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> {currentYear} Forecast
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9A96E', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Budget {currentYear}
          </Typography>
          {revenueComparisonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={revenueComparisonData} margin={{ left: 8, right: 8, top: 20 }} barCategoryGap="18%" barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmtCurrency(v)} />
                <RTooltip
                  formatter={(value: number, name: string) => [fmtCurrency(value), name]}
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(label: string) => `${label}`}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="prev_year_actual" fill="#8B7355" name={`${previousYear} Actuals`} radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="prev_year_actual" position="top" fontSize={8} formatter={(v: number) => v > 0 ? fmtCurrency(v) : ''} />
                </Bar>
                <Bar dataKey="current_year_actual" stackId="currentYear" fill="#722F37" name={`${currentYear} Actual`} radius={[3, 3, 0, 0]} />
                <Bar dataKey="current_year_forecast" stackId="currentYear" fill="#B76E79" name={`${currentYear} Forecast`} radius={[3, 3, 0, 0]} />
                <Bar dataKey="budget" fill="#C9A96E" name={`Budget ${currentYear}`} radius={[3, 3, 0, 0]} opacity={0.8}>
                  <LabelList dataKey="budget" position="top" fontSize={8} formatter={(v: number) => v > 0 ? fmtCurrency(v) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'var(--color-text-muted)' }}>No revenue comparison data available</Typography>
            </Box>
          )}
        </Paper>

        {/* Cumulative Sales Comparison */}
        <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', mt: 3 }}>
          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
            Cumulative Sales Comparison
          </Typography>
          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#8B7355', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> {previousYear}
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#722F37', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> {currentYear} Actual + Forecast
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9A96E', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Budget {currentYear}
          </Typography>
          {cumulativeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={cumulativeData} margin={{ left: 8, right: 8, top: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmtCurrency(v)} />
                <RTooltip
                  formatter={(value: number, name: string) => [fmtCurrency(value), name]}
                  contentStyle={chartTooltipStyle}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line type="monotone" dataKey={`${previousYear}`} stroke="#8B7355" strokeWidth={2} dot={{ r: 3, fill: '#8B7355' }} name={`${previousYear}`} />
                <Line type="monotone" dataKey={`${currentYear} Actual + Forecast`} stroke="#722F37" strokeWidth={2.5} dot={{ r: 4, fill: '#722F37' }} name={`${currentYear} Actual + Forecast`} />
                <Line type="monotone" dataKey={`Budget ${currentYear}`} stroke="#C9A96E" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: '#C9A96E' }} name={`Budget ${currentYear}`} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'var(--color-text-muted)' }}>No cumulative data available</Typography>
            </Box>
          )}
        </Paper>
      </CollapsibleSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ─── SECTION 2: Distribution Performance ─── */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Distribution Performance"
        forceOpen={exporting}
        headerContent={
          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            <Grid item xs={6} sm={3}>
              <KPICard label="Categories" value={fmtNumber(categoryData.length || metrics.quick_metrics?.total_products || 0)} subtext={categoryData.length > 0 ? `Top: ${categoryData[0]?.name || '—'}` : 'product categories'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard label="Cities" value={fmtNumber(regionMarketShare.length)} subtext={regionMarketShare.length > 0 ? `Top: ${regionMarketShare[0]?.city || '—'}` : 'store locations'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard label="Total Brands" value={fmtNumber(topBrands.length)} subtext={topBrands.length > 0 ? `#1: ${topBrands[0]?.brand || '—'}` : 'active brands'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard label="Total Stores" value={fmtNumber(metrics.quick_metrics?.total_stores)} subtext="active locations" />
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={3}>
          {/* Category Performance */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 360 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 2 }}>Category Performance</Typography>
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="88%">
                  <PieChart>
                    <Pie
                      data={categoryData.slice(0, 6)}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={2}
                      label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#C4C4BF', strokeWidth: 1 }}
                    >
                      {categoryData.slice(0, 6).map((_: any, idx: number) => (
                        <Cell key={idx} fill={['#722F37', '#B76E79', '#C9A96E', '#8B7355', '#8B6F8B', '#DEB5A0'][idx % 6]} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v: number) => fmtCurrency(v)} contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '85%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No category data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* City Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 360 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 2 }}>City Distribution</Typography>
              {regionMarketShare.length > 0 ? (
                <ResponsiveContainer width="100%" height="88%">
                  <BarChart data={regionMarketShare}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                    <XAxis dataKey="city" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} angle={-35} textAnchor="end" height={60} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} />
                    <RTooltip formatter={(v: number) => fmtCurrency(v)} contentStyle={chartTooltipStyle} />
                    <Bar dataKey="revenue" fill="#B76E79" name={`Revenue ${fmtCurrencyLabel()}`} radius={[3, 3, 0, 0]} barSize={28}>
                      <LabelList dataKey="revenue" position="top" fontSize={9} formatter={(v: number) => fmtCurrency(v)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '85%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No market data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Top Brands — 4 criticality cards: All, Makeup, Perfume, Skincare.
              Each card surfaces a green/orange/red health status with business
              impact, and a dropdown revealing that card's top 10 brands. */}
          {(() => {
            const hasActiveFilters = filters.brands.length > 0 || filters.categories.length > 0 || filters.stores.length > 0 || filters.cities.length > 0;
            const filterSummary = hasActiveFilters
              ? `Filtered by${filters.brands.length > 0 ? ` ${filters.brands.length} brand${filters.brands.length > 1 ? 's' : ''}` : ''}${filters.categories.length > 0 ? ` ${filters.categories.length} categor${filters.categories.length > 1 ? 'ies' : 'y'}` : ''}${filters.stores.length > 0 ? ` ${filters.stores.length} store${filters.stores.length > 1 ? 's' : ''}` : ''}${filters.cities.length > 0 ? ` ${filters.cities.length} cit${filters.cities.length > 1 ? 'ies' : 'y'}` : ''}`
              : undefined;
            return [
              { title: 'Top 10 Brands — All Categories', data: topBrands },
              { title: 'Top 10 — Makeup', data: (topBrandsMakeup || []).slice(0, 10) },
              { title: 'Top 10 — Perfume', data: (topBrandsFragrance || []).slice(0, 10) },
              { title: 'Top 10 — Skincare', data: (topBrandsSkincare || []).slice(0, 10) },
            ].map(({ title, data: brands }) => (
              <BrandCriticalityCard
                key={title}
                title={title}
                brands={brands}
                filterSummary={filterSummary}
                fmtCurrency={fmtCurrency}
              />
            ));
          })()}
        </Grid>
      </CollapsibleSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ─── SECTION 3: Inventory Status ─── */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Inventory Status"
        defaultOpen={false}
        forceOpen={exporting}
        headerContent={
          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            <Grid item xs={6} sm={2.4}>
              <KPICard label="Inventory Value" value={fmtCurrency(metrics.kpis?.total_inventory_value)} trend={metrics.kpis?.inventory_value_trend} />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <KPICard label="Inventory Quantity" value={fmtNumber(inventoryQuantity)} subtext="units in stock" />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <KPICard label="Stock Coverage" value={`${metrics.kpis?.stock_coverage_days?.toFixed(0) || 0} days`} trend={metrics.kpis?.stock_coverage_trend} />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <KPICard label="Service Level" value={fmtPct(metrics.kpis?.fill_rate_percentage)} trend={metrics.kpis?.fill_rate_trend} onClick={() => handleKPIDrillDown('fillRate')} />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <KPICard
                label="Out of Stock"
                value={`${fmtNumber(stockoutCount)} (${stockoutPct}%)`}
                subtext={`of ${fmtNumber(totalPositions)} stock positions`}
                accent
              />
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={3}>
          {/* Stock Evolution by Lifecycle — Monthly Stacked Bar */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 420 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Evolution by Lifecycle</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                Monthly stock quantity — actual &amp; projected
              </Typography>
              {(stockByLifecycle?.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stockByLifecycle!.data} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`} />
                    <RTooltip
                      formatter={(value: number, name: string) => [fmtNumber(value) + ' units', name.replace('_qty', '')]}
                      contentStyle={chartTooltipStyle}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} formatter={(val: string) => val.replace('_qty', '')} />
                    {(stockByLifecycle?.groups || []).map((grp: string, idx: number) => {
                      const colors: Record<string, string> = {
                        NEW: '#B76E79', GROWTH: '#8B6F8B', MATURE: '#722F37',
                        DECLINING: '#C9A96E', PHASE_OUT: '#D4A574', DISCONTINUED: '#9CA3AF',
                      };
                      const isLast = idx === (stockByLifecycle?.groups || []).length - 1;
                      return (
                        <Bar key={grp} dataKey={`${grp}_qty`} stackId="lifecycle" name={grp}
                          fill={colors[grp] || ['#722F37', '#B76E79', '#C9A96E', '#8B6F8B', '#DEB5A0', '#8B7355'][idx % 6]}
                          radius={isLast ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                        >
                          {isLast && (
                            <LabelList
                              valueAccessor={(entry: any) => {
                                const total = (stockByLifecycle?.groups || []).reduce((sum: number, g: string) => sum + (entry[`${g}_qty`] || 0), 0);
                                return total >= 1000000 ? `${(total / 1000000).toFixed(1)}M` : total >= 1000 ? `${(total / 1000).toFixed(0)}K` : total;
                              }}
                              position="top" fontSize={8} fill="#8A8A85"
                            />
                          )}
                        </Bar>
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No lifecycle stock data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Stock Evolution by ABC — Monthly Stacked Bar */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 420 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Evolution by ABC Classification</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                Monthly stock value — actual &amp; projected
              </Typography>
              {(stockByAbc?.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stockByAbc!.data} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmtCurrency(v)} />
                    <RTooltip
                      formatter={(value: number, name: string) => [fmtCurrency(value), 'Class ' + name.replace('_val', '')]}
                      contentStyle={chartTooltipStyle}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} formatter={(val: string) => 'Class ' + val.replace('_val', '')} />
                    {(stockByAbc?.groups || []).map((grp: string, idx: number) => {
                      const colors: Record<string, string> = {
                        A: '#722F37', B: '#C9A96E', C: '#8B7355', D: '#9CA3AF', E: '#D1D5DB',
                      };
                      const isLast = idx === (stockByAbc?.groups || []).length - 1;
                      return (
                        <Bar key={grp} dataKey={`${grp}_val`} stackId="abc" name={grp}
                          fill={colors[grp] || ['#722F37', '#B76E79', '#C9A96E', '#8B6F8B', '#DEB5A0'][idx % 5]}
                          radius={isLast ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                        >
                          {isLast && (
                            <LabelList
                              valueAccessor={(entry: any) => {
                                const total = (stockByAbc?.groups || []).reduce((sum: number, g: string) => sum + (entry[`${g}_val`] || 0), 0);
                                return total >= 1000000 ? `${(total / 1000000).toFixed(1)}M` : total >= 1000 ? `${(total / 1000).toFixed(0)}K` : total;
                              }}
                              position="top" fontSize={8} fill="#8A8A85"
                            />
                          )}
                        </Bar>
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No ABC stock data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Alerts */}
          <Grid item xs={12}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)' }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 2 }}>Stock Alerts</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'var(--color-danger-bg)', borderRadius: 'var(--radius-md)' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-danger)', mb: 0.5 }}>Stockout Items</Typography>
                    <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNumber(stockoutCount)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'var(--color-warning-bg)', borderRadius: 'var(--radius-md)' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-warning)', mb: 0.5 }}>Low Stock</Typography>
                    <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--color-warning)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNumber(metrics.quick_metrics?.low_stock_items || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'var(--color-success-bg)', borderRadius: 'var(--radius-md)' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-success)', mb: 0.5 }}>Optimal</Typography>
                    <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--color-success)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNumber(Math.max(0, totalSkus - stockoutCount - (metrics.quick_metrics?.low_stock_items || 0)))}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </CollapsibleSection>

      </div>

      {/* Drill-Down Modal */}
      <DrillDownModal
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        title={drillDownConfig.title}
        data={drillDownConfig.data}
        chartConfig={drillDownConfig.chartConfig}
        columns={drillDownConfig.columns}
        insights={drillDownConfig.insights}
      />
    </Box>
  );
};

export default Dashboard;
